import { DomQueries } from 'lib-bb-dom-queries';
import { RawPageConfig } from 'lib-bb-page-config';
import { Pubsub } from 'lib-bb-pubsub';
import { Item } from 'lib-bb-render-model-parser';
import { ContainerApi } from 'render-bb-container';

declare global {
  interface Window {
    BB: {
      config: RawPageConfig
    };
  }
} 

export interface ContainerCreateInput {
  domQueries: DomQueries;
  pubsub: Pubsub;
  refresh: Function;
}

/**
 * Turns querySelectorAll method from NodeList into Array so it is iterable
 * @param {HTMLElement} element to perform querySelectorAll on
 * @param {string} selector which is passed to querySelectorAll
 * @returns {HTMLElement[]} elements array
 */
function querySelectorAll<T extends HTMLElement = HTMLElement>(element: HTMLElement, selector: string): T[] {
  const nodeList = element.querySelectorAll(selector);
  const nodeListArray = Array.prototype.slice.call(nodeList, 0);
  return nodeListArray;
}

/**
 * This method is invoked for the widget as of the render-bb-container render strategy
 * @param {ContainerCreateInput} properties object containing domQueries, pubsub, refresh
 * @return {Create} construction object for onBootstrap, onUpdate, and onRemove events
 */
export function create({ domQueries, refresh }: ContainerCreateInput): ContainerApi {

  /**
   * Get widget chrome by instance name and return the widget body HTML element
   * @param {string} name of the widget
   * @return {HTMLElement} widget body HTML element
   */
  function getWidgetBody(name: string): HTMLElement {
    return domQueries.getItemElementByName(name).children[0] as HTMLElement;
  }

  /**
   * Bootstrapping of the widget which:
   * - Changes empty images for placeholder images
   * - Add the Lean Page wrapper class which adds placeholder for empty tags (with CSS)
   * - Removes the ICE wrapper class which hides the content of the widget
   * @param {Widget} props properties object containing the widget name, properties, etc
   */
  async function onBootstrap({ name }: Item) {
    // CONTENT-88 With a new custom render strategy this script can only be imported during design mode
    if (window.BB.config.designmode !== 'true') {
      return;
    }

    const widgetBody = getWidgetBody(name);

    querySelectorAll<HTMLImageElement>(widgetBody, 'img')
      .filter(image => {
        // Use getAttribute as image.src will append host for absolute URL
        const src = image.getAttribute('src');
        // CONTENT-88 A new custom render strategy can inject the page config, removing the need for window.BB.config
        return !src || src === window.BB.config.apiRoot;
      })
      .forEach(image => {
        // Use base64 of /catalog/widget-bb-content/media/transparent-320x180.png
        // This way the resource doesn't has to be loaded, which could cause a flicker when widget becomes visible again
        // A transparent image is used so the image sizing works similar to a real image
        image.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAUAAAAC0CAYAAADl5PURAAABS2lUWHRYTUw6Y29tLmFkb2JlLnhtcAAAAAAAPD94cGFja2V0IGJlZ2luPSLvu78iIGlkPSJXNU0wTXBDZWhpSHpyZVN6TlRjemtjOWQiPz4KPHg6eG1wbWV0YSB4bWxuczp4PSJhZG9iZTpuczptZXRhLyIgeDp4bXB0az0iQWRvYmUgWE1QIENvcmUgNS42LWMxMzggNzkuMTU5ODI0LCAyMDE2LzA5LzE0LTAxOjA5OjAxICAgICAgICAiPgogPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4KICA8cmRmOkRlc2NyaXB0aW9uIHJkZjphYm91dD0iIi8+CiA8L3JkZjpSREY+CjwveDp4bXBtZXRhPgo8P3hwYWNrZXQgZW5kPSJyIj8+IEmuOgAAAmhJREFUeJzt1DkBACAQwDAe/2ZPATIYmijo1D0zdwEEnd8BAL8YIJBlgECWAQJZBghkGSCQZYBAlgECWQYIZBkgkGWAQJYBAlkGCGQZIJBlgECWAQJZBghkGSCQZYBAlgECWQYIZBkgkGWAQJYBAlkGCGQZIJBlgECWAQJZBghkGSCQZYBAlgECWQYIZBkgkGWAQJYBAlkGCGQZIJBlgECWAQJZBghkGSCQZYBAlgECWQYIZBkgkGWAQJYBAlkGCGQZIJBlgECWAQJZBghkGSCQZYBAlgECWQYIZBkgkGWAQJYBAlkGCGQZIJBlgECWAQJZBghkGSCQZYBAlgECWQYIZBkgkGWAQJYBAlkGCGQZIJBlgECWAQJZBghkGSCQZYBAlgECWQYIZBkgkGWAQJYBAlkGCGQZIJBlgECWAQJZBghkGSCQZYBAlgECWQYIZBkgkGWAQJYBAlkGCGQZIJBlgECWAQJZBghkGSCQZYBAlgECWQYIZBkgkGWAQJYBAlkGCGQZIJBlgECWAQJZBghkGSCQZYBAlgECWQYIZBkgkGWAQJYBAlkGCGQZIJBlgECWAQJZBghkGSCQZYBAlgECWQYIZBkgkGWAQJYBAlkGCGQZIJBlgECWAQJZBghkGSCQZYBAlgECWQYIZBkgkGWAQJYBAlkGCGQZIJBlgECWAQJZBghkGSCQZYBAlgECWQYIZBkgkGWAQJYBAlkGCGQZIJBlgECWAQJZBghkGSCQZYBAlgECWQYIZBkgkGWAQJYBAlkGCGQZIJBlgECWAQJZBghkGSCQZYBAlgECWQYIZBkgkGWAQNYDbb4EQU3NydYAAAAASUVORK5CYII=';
        // Placeholder styling is done with CSS
        image.classList.add('widget-bb-content-lean-page-placeholder-image');
      });

    widgetBody.classList.add('widget-bb-content-lean-page');
    widgetBody.classList.remove('be-ice-widget');
  }

  /**
   * Updating the widget should refresh the widget.
   * This will trigger the widget to be re-rendered on the server.
   */
  async function onUpdate({ name }) {
    refresh(name);
  }

  /**
   * No cleanup has to happen on removing the widget.
   */
  async function onRemove() {}

  return {
    onBootstrap: onBootstrap,
    onUpdate: onUpdate,
    onRemove: onRemove
  };
}



// WEBPACK FOOTER //
// ./scripts/index.ts